import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Player, BigPlayButton, ControlBar } from 'video-react';
import 'video-react/dist/video-react.css';

const parseId = (id: string) => {
  if (!id || id.length < 4) return null;

  const videoId = id[0];
  const langId = id[3];

  return { videoId, langId };
};

export default function FinalAvatarPage() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const [videoPath, setVideoPath] = useState('/videos/avatar.mp4');
  const [videoId, setVideoId] = useState('');
  const [langId, setLangId] = useState('');

  useEffect(() => {
    const id = searchParams.get('id');
    if (id) {
      const parsed = parseId(id);
      if (parsed) {
        const { videoId, langId } = parsed;
        setVideoId(videoId);
        setLangId(langId);
        setVideoPath(`/videos/${videoId}-${langId}.mp4`);
      }
    }
  }, [searchParams]);

  const handleShare = async (type: 'email' | 'copy' | 'download') => {
    const avatarUrl = window.location.href;

    switch (type) {
      case 'email':
        window.location.href = `mailto:?subject=Check out my Symphony Avatar&body=${avatarUrl}`;
        break;
      case 'copy':
        await navigator.clipboard.writeText(avatarUrl);
        break;
      case 'download':
        const link = document.createElement('a');
        link.href = videoPath;
        link.download = `${videoPath.split('/').pop()}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        break;
    }
    setIsDropdownOpen(false);
  };

  return (
    <div className="bg-black md:flex justify-center items-center">
      <div className="flex flex-col items-center bg-black md:w-[520px] py-10 px-5">
        <img
          src="https://cdn.builder.io/api/v1/image/assets/7951366160e541d0bb67ff4b832b0c72/dc41759376d9947110922090cdea348f25f7898b?placeholderIfAbsent=true"
          alt="Logo"
          className="object-contain w-[260px]"
        />

        <div className="flex flex-col items-center mt-5">
          <div className="w-full h-[510px] bg-black">
            <Player
              playsInline
              fluid={false}
              height={510}
              autoPlay={true}
             // muted={true}
            >
              <source src={`/videos/${videoId}-${langId}.mp4`} />
              <ControlBar />
              <BigPlayButton position="center" />
            </Player>
          </div>
        </div>
        {/* Share Button with Dropdown */}
        <div className="relative mb-12 mt-8">
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center justify-center gap-2 border border-1 border-white text-black font-semibold px-5 py-3 rounded-lg  shadow-lg"
          >
            <svg
              width="40"
              height="32"
              viewBox="0 0 43 35"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M42.9994 12.4728C43.0087 13.0088 42.7682 13.5218 42.3474 13.8562L29.5417 24.5197C28.7694 25.161 27.6179 25.0648 26.9704 24.2998C26.6929 23.9746 26.5403 23.5624 26.5403 23.1364V20.1498C26.5403 19.7147 26.1981 19.3574 25.7634 19.3253C19.5987 18.8856 14.3174 20.0582 10.0627 22.8066C5.97911 25.445 2.8436 29.54 0.720886 34.9954C-1.12435 27.7673 0.619144 20.0995 5.41953 14.3554C10.1228 8.6435 17.1615 5.3226 24.6026 5.31344C24.9541 5.31344 25.3102 5.3226 25.6662 5.33634C26.1287 5.35467 26.5218 5.00196 26.5403 4.54391C26.5403 4.53017 26.5403 4.521 26.5403 4.50726V1.80932C26.5403 0.810758 27.3589 0 28.367 0C28.7971 0 29.2134 0.151158 29.5463 0.425991L42.352 11.0895C42.7728 11.4285 43.0133 11.9369 43.0041 12.4728H42.9994Z"
                fill="#25F4EE"
              />
            </svg>

            <span className="text-white text-[18px] font-semibold">
              Share it with your team!
            </span>
          </button>

          {isDropdownOpen && (
            <div className="absolute left-1/2 transform -translate-x-1/2 mt-2 w-48 bg-white rounded-lg shadow-xl z-50">
              <div className="py-1">
                <button
                  onClick={() => handleShare('email')}
                  className="w-full text-left px-4 py-2 text-lg text-black flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Share via Email
                </button>
                <button
                  onClick={() => handleShare('copy')}
                  className="w-full text-left px-4 py-2 text-lg text-black flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                  Copy Link
                </button>
                <button
                  onClick={() => handleShare('download')}
                  className="w-full text-left px-4 py-2 text-lg text-black flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download Video
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Learn More Section */}
        <div className="text-center my-5">
          <p className="text-white text-[16px] mb-2">
            Want to learn more about{" "}
            <span className="text-[#25F4EE] font-semibold">
              Symphony Creative Studio?
            </span>
            <br />
            Try it out here - it's
            super simple
            to get started:
          </p>
          <a
            href="https://ads.tiktok.com/creative/creativestudio/home/en"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block text-[16px] bg-[#25F4EE] text-black font-normal italic underline px-5 py-3 rounded-lg mt-4 shadow hover:bg-[#1ad1cc] transition"
          >
            Symphony Creative Studio
          </a>
        </div>
      </div>
    </div>
  );
}

